import api from '~/libs/api';
import { errorMessages } from '~/constants/errorMessages.ts';
import { useSelector } from 'react-redux';
import { RootState } from '~/store/store.ts';
import { useQuery } from '@tanstack/react-query';
import { TKvedResponse } from '~/queries/types.ts';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { useMemo } from 'react';

const getKved = (appId: string | number) => async () => {
  try {
    const { data } = await api.get(`/identification/edr-nais/kved`, {
      params: { 'app-id': appId },
    });
    return data;
  } catch {
    throw Error(errorMessages.SERVICE_UNAVAILABLE);
  }
};

export const useKvedsQuery = () => {
  const appId = useSelector((state: RootState) => state.identification.formData.applicationId);
  const { data, error, isLoading, isError } = useQuery<TKvedResponse>({
    queryKey: ['kved', appId],
    queryFn: getKved(appId!),
    staleTime: 30 * 60 * 1000,
  });

  const activityKinds: TAutocompleteOption[] = useMemo(() => {
    const activity = data?.activityKinds;
    if (!activity) return [];
    return activity.map((kved) => ({
      value: kved.id?.toString(),
      label: `${kved.code} - ${kved.name}`,
    }));
  }, [data]);

  return {
    activityKinds,
    edrRequest: data?.edrRequest ?? false,
    kveds: data,
    error,
    isLoading,
    isError,
  };
};
